import React, { useEffect, useLayoutEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import { BASE_URL } from "../Constants";
import ShowAllContentBySubCategoryComponent from "./ShowSubCategoryComponent";
import SubCategorySkeleton from "./SubCategorySkeleton";
import FooterComponent from "./FooterComponent";
import {
  saveSubCategoryScroll,
  getSubCategoryScroll,
  clearSubCategoryScroll,
} from "./utils/scrollManager";

function ShowAllContentBySubCategory() {
  const { scid } = useParams();
  const location = useLocation();

  // Continuously sync scroll position as user scrolls
  useEffect(() => {
    const handleScroll = () => {
      saveSubCategoryScroll(scid);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      saveSubCategoryScroll(scid);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scid]);

  const { data, isLoading, isError } = useQuery(
    ["contentBySubcategory", scid],
    async () => {
      const [contentResponse, subcategoryResponse] = await Promise.all([
        axios.get(BASE_URL + `/api/content/sccache/${scid}`, {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }),
        axios.get(BASE_URL + `/api/subcategory/${scid}`, {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }),
      ]);

      return {
        content: contentResponse.data,
        subcategory: subcategoryResponse.data,
      };
    },
    {
      staleTime: 60000, // Set the stale time to 60 seconds (adjust as needed)
      cacheTime: 600000, // Set the cache time to 10 minutes (adjust as needed)
      onSuccess: (data) => {
        data.subcategory.titleSecondary
          ? (document.title =
              data.subcategory.titlePrimary +
              " | " +
              data.subcategory.titleSecondary +
              " | Gangadwar.in | Gangadwar Bhajnawali")
          : (document.title =
              data.subcategory.titlePrimary +
              " | Gangadwar.in | Gangadwar Bhajnawali");
      },
    }
  );

  // Pre-paint scroll restoration before the browser renders the frame
  useLayoutEffect(() => {
    if (!isLoading && data?.content) {
      if (location.state?.resetScroll) {
        clearSubCategoryScroll(scid);
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      } else {
        const savedY = getSubCategoryScroll(scid);
        if (savedY > 0) {
          const doScroll = () => {
            window.scrollTo({ top: savedY, left: 0, behavior: "instant" });
            if (document.documentElement) document.documentElement.scrollTop = savedY;
            if (document.body) document.body.scrollTop = savedY;
          };

          doScroll();
          requestAnimationFrame(doScroll);
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        }
      }
    }
  }, [isLoading, data, scid, location.state]);

  return (
    <>
      {isLoading ? (
        <>
          <SubCategorySkeleton />
          <FooterComponent />
        </>
      ) : isError ? (
        <p className="text-center py-5">Error: An error occurred while fetching data.</p>
      ) : (
        <>
          <ShowAllContentBySubCategoryComponent
            dataAll={data.content}
            id={data.id}
            subcategory={data.subcategory}
          />

          <FooterComponent />
        </>
      )}
    </>
  );
}

export default ShowAllContentBySubCategory;
